import { useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { InView } from "react-intersection-observer";
import { Loader2 } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  SearchInput,
  Tabs,
  TabsList,
  TabsTrigger,
  VisuallyHidden,
} from "web-design-system";
import { handleError } from "web-core/utils";
import {
  useFetchSchemesByIsin,
  useInfiniteFunds,
  useUpdatePortfolioReviewLine,
} from "creso-core/hooks";
import { Fund, PortfolioReviewLine } from "creso-core/types";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
} from "creso-core/utils";
import {
  CategoryPicker,
  findCategoryByLabel,
  useCategoryOptions,
} from "./category-picker";
import { EditColumnsMenu } from "./edit-columns-menu";
import { FavouritesEmptyState } from "./favourites-empty-state";
import { FundCell } from "./fund-badges";
import { SWITCH_COLUMNS, SwitchColumnKey } from "./holding-columns";

export function SwitchFundDialog({
  reviewId,
  line,
  open,
  onOpenChange,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] max-w-5xl flex-col gap-6">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle>Switch to a new fund</DialogTitle>
          </VisuallyHidden>
          <span className="text-lg font-semibold">Switch to a new fund</span>
          <DialogDescription className="sr-only">
            Pick a fund to switch into.
          </DialogDescription>
        </DialogHeader>
        {/* Mounted only while open, so search and category reset on reopen. */}
        {open ? (
          <SwitchCandidates
            reviewId={reviewId}
            line={line}
            onSwitched={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SwitchCandidates({
  reviewId,
  line,
  onSwitched,
}: {
  reviewId: number;
  line: PortfolioReviewLine;
  onSwitched: () => void;
}) {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "favourites">("all");
  const showFavourites = tab === "favourites";
  const [debouncedSearch] = useDebounceValue(search, 400);
  const [pickedCategories, setPickedCategories] = useState<string[] | null>(
    null
  );
  const [visibleColumns, setVisibleColumns] = useState<SwitchColumnKey[]>(
    SWITCH_COLUMNS.filter((c) => c.group === "visible").map((c) => c.key)
  );
  const [pickingFundId, setPickingFundId] = useState<string | null>(null);
  const { mutateAsync: updateLine } = useUpdatePortfolioReviewLine();
  const fetchSchemesByIsin = useFetchSchemesByIsin("all");

  // Starts on the holding's own category until the user changes it.
  const { options, isPending: isCategoriesPending } = useCategoryOptions();
  const sourceCategory = findCategoryByLabel(options, line.source_category);
  const categories =
    pickedCategories ?? (sourceCategory ? [sourceCategory.id] : []);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteFunds(
      {
        pageNo: 1,
        pageSize: 20,
        search: debouncedSearch,
        // Favourites are listed whole, whatever the category.
        filters: showFavourites
          ? { favourites: "true" }
          : { fund_level_category_code_in: categories },
        // Ranks only compare funds within one category.
        sort:
          !showFavourites && categories.length === 1
            ? [{ id: "rankings__category_rank", desc: false }]
            : undefined,
      },
      // Wait for the default category, so the list is not fetched twice.
      { maxPages: 0, enabled: !isCategoriesPending }
    );
  const funds = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data]
  );

  const columns = SWITCH_COLUMNS.filter((c) => visibleColumns.includes(c.key));
  // A search with no matches is not the same as having no favourites.
  const noFavourites =
    showFavourites && !debouncedSearch && !isLoading && !funds.length;

  const pick = async (fund: Fund) => {
    setPickingFundId(fund.fund_id);
    try {
      // Review lines point at transactable schemes, so map the fund by ISIN.
      const [scheme] = await fetchSchemesByIsin([fund.isin]);
      if (!scheme) throw new Error(`${fund.fund_name} cannot be purchased`);
      await updateLine({
        reviewId,
        lineId: line.id,
        action: "SWITCH",
        target_fund_scheme: scheme.fund_scheme_id ?? scheme.id,
      });
      onSwitched();
    } catch (error) {
      handleError(error, "Could not switch the fund");
    } finally {
      setPickingFundId(null);
    }
  };

  return (
    <div className="flex min-h-0 flex-col gap-6 flex-1">
      <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
        <span className="text-xs font-semibold text-muted-foreground">
          Switch from
        </span>
        <div className="flex items-center justify-between gap-4">
          <FundCell
            className="max-w-100"
            name={line.source_scheme_name}
            category={line.source_category}
            amcCode={line.amc_morningstar_code}
            fundId={line.source_fund_id}
          />
          <span className="font-semibold">
            {convertToCurrency(line.current_value)}
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-col gap-4 flex-1">
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as typeof tab)}
        >
          <TabsList>
            <TabsTrigger value="all">All funds</TabsTrigger>
            <TabsTrigger value="favourites">Favourites</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex flex-row items-center gap-4">
          <div className="w-85">
            <SearchInput
              placeholder="Search for funds"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {showFavourites ? null : (
            <CategoryPicker value={categories} onChange={setPickedCategories} />
          )}
          <div className="ml-auto">
            <EditColumnsMenu
              columns={SWITCH_COLUMNS}
              visible={visibleColumns}
              onChange={setVisibleColumns}
            />
          </div>
        </div>

        {noFavourites ? (
          <FavouritesEmptyState />
        ) : (
          <div className="min-h-0 overflow-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr>
                  <th className="p-4 text-left font-semibold">Fund Name</th>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="whitespace-nowrap p-4 text-right font-semibold"
                    >
                      {column.label}
                    </th>
                  ))}
                  <th className="p-4" />
                </tr>
              </thead>
              <tbody>
                {funds.map((fund) => (
                  <FundRow
                    key={fund.fund_id}
                    fund={fund}
                    columns={columns.map((c) => c.key)}
                    isSource={fund.fund_id === line.source_fund_id}
                    isCurrent={fund.fund_id === line.target_fund_id}
                    isPicking={pickingFundId === fund.fund_id}
                    disabled={pickingFundId !== null}
                    onPick={() => pick(fund)}
                  />
                ))}
              </tbody>
            </table>
            {!funds.length ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                {showFavourites ? "No favourite funds found" : "No funds found"}
              </p>
            ) : null}
            {hasNextPage ? (
              <InView
                as="div"
                onChange={(inView) => {
                  if (inView && !isFetchingNextPage) fetchNextPage();
                }}
                className="flex justify-center p-4"
              >
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
              </InView>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function FundRow({
  fund,
  columns,
  isSource,
  isCurrent,
  isPicking,
  disabled,
  onPick,
}: {
  fund: Fund;
  columns: SwitchColumnKey[];
  isSource: boolean;
  isCurrent: boolean;
  isPicking: boolean;
  disabled: boolean;
  onPick: () => void;
}) {
  const cells: Record<SwitchColumnKey, React.ReactNode> = {
    rank: fund.ranking?.category_rank ?? "-",
    aum: formatAsCrore(fund.assets_under_management, 0),
    return_1y: formatAsPercentage(fund.performance?.return_1_year, "-"),
    return_3y: formatAsPercentage(fund.performance?.return_3_year, "-"),
    return_5y: formatAsPercentage(fund.performance?.return_5_year, "-"),
  };

  return (
    <tr className="border-t border-border">
      <td className="px-4 py-3">
        <FundCell
          fundId={fund.fund_id}
          name={fund.fund_name}
          category={fund.fund_level_category_name}
          amcCode={fund.provider_company_id}
        />
      </td>
      {columns.map((key) => (
        <td key={key} className="whitespace-nowrap px-4 py-3 text-right">
          {cells[key]}
        </td>
      ))}
      <td className="px-4 py-3 text-right">
        <Button
          variant="outline"
          className="w-30 shrink-0"
          disabled={isSource || isCurrent || disabled}
          onClick={onPick}
        >
          {isPicking ? (
            <Loader2 className="animate-spin" />
          ) : isSource ? (
            "Current fund"
          ) : isCurrent ? (
            "Selected"
          ) : (
            "Recommend"
          )}
        </Button>
      </td>
    </tr>
  );
}
